import { FC, useContext, useEffect, useState } from "react";
import { map } from "lodash";
import { useNavigate } from "react-router-dom";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  ErrorScreen,
  InputValidated,
  Loader,
  Row,
  RowSize,
  toast,
} from "@/components";
import { useRequestState } from "@/hooks";
import { debounce } from "@/utils/debounce";
import { CreateSubcontractor, SubcontractorItem } from "./components";
import {
  INPUT_DELAY,
  SUB_CONTRACTOR_PAGE_ROOT_PATH,
  SubcontractorPage,
} from "@/constants";
import styles from "./styles.scss";
import { AuthContext } from "@/guards/auth/auth-context";
import {
  requestAddSubcontractor,
  requestSearchSubcontractors,
  SubContractor,
} from "@/services/api";
import { logger } from "@/utils";
import { selectUserStore, useBoundStore } from "../../../../services";

export const SubcontractorSearch: FC = () => {
  const { account } = useContext(AuthContext);

  const {
    data: subcontractors,
    setInProgress,
    setSuccess,
    setError,
    isLoading,
    error,
  } = useRequestState<SubContractor[] | null>({
    isLoading: false,
    data: null,
  });

  const { user } = useBoundStore(selectUserStore);

  const [searchString, setSearchString] = useState("");
  const handleSearchChange = (value: string) => setSearchString(value);

  const navigate = useNavigate();

  const handleCreateSubcontractor = () =>
    navigate(`${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.Edit}`);

  const handleAddSubcontractor = async (subcontractor: SubContractor) => {
    const toastId = toast.info("Adding subcontractor...");

    try {
      const controller = new AbortController();
      await requestAddSubcontractor(
        { email: account?.username ?? "" },
        subcontractor,
        user?.Contractor?.ContractorId ?? "",
        { signal: controller.signal },
      );
      toast.success("Subcontractor was added", { id: toastId });
    } catch (error) {
      logger.error(error as string);
      toast.error("Something went wrong", { id: toastId });
    }

    navigate(`${SUB_CONTRACTOR_PAGE_ROOT_PATH}/${SubcontractorPage.List}`);
  };

  useEffect(() => {
    const search = searchString.trim();
    const controller = new AbortController();

    if (search) {
      debounce(async () => {
        setInProgress();

        return requestSearchSubcontractors(
          { email: account?.username ?? "", search },
          {
            signal: controller.signal,
          },
        )
          .then(response => {
            setSuccess(response?.Data?.SubContractors);
          })
          .catch(error => {
            setError({ message: error.message || "Unknown Error" });
          });
      }, INPUT_DELAY)();
    }

    return () => {
      controller.abort();
    };
  }, [account, searchString, setError, setSuccess, setInProgress]);

  if (error) {
    return (
      <div className={styles.noResultsContainer}>
        <ErrorScreen
          subtitle="Can't loading subcontractors search list"
          error={error}
        />
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <Row size={RowSize.Small}>
        <div className={styles.pageHeading}>Add Subcontractor</div>
      </Row>

      <Row size={RowSize.Small}>
        <InputValidated
          label="Company"
          placeholder="Search for registered Contractor by Company ID or Name"
          type="string"
          value={searchString}
          onChange={e => handleSearchChange(e.target.value)}
        />
      </Row>

      <Row size={RowSize.Medium}>
        {!!(isLoading && searchString?.length) && (
          <div className={styles.loaderWrapper}>
            <Loader />
          </div>
        )}

        {!!(!!subcontractors?.length && !isLoading && searchString?.length) && (
          <>
            <Row size={RowSize.Small}>
              <div className={styles.listHeading}>Existing subcontractors</div>
            </Row>

            {map(subcontractors, (sub, i) => (
              <Row size={RowSize.Small} key={i}>
                <SubcontractorItem
                  subcontractor={sub}
                  onAddSubcontractor={handleAddSubcontractor}
                />
              </Row>
            ))}

            <Row
              size={RowSize.ExtraLarge}
              className={styles.createSubcontractorBtnContainer}
            >
              <Button
                variant={ButtonVariant.Secondary}
                size={ButtonSize.Small}
                className={styles.createSubcontractorBtn}
                onClick={handleCreateSubcontractor}
              >
                Create new Subcontractor
              </Button>
            </Row>
          </>
        )}
        {!subcontractors?.length && !isLoading && searchString && (
          <Row size={RowSize.Small}>
            <CreateSubcontractor />
          </Row>
        )}
      </Row>
    </div>
  );
};
